import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import { CALL_API } from 'redux-api-middleware';
import { PHARMACY_API, USER_API } from '../../../../config/endpoints';
import { getAPIErrorCode, getAPIErrorMessage } from '../../api-error';
import { chainAPIActions } from '../../middleware/chain';
import { ROUTE } from '../../middleware/route';
import paths from '../../../routes/paths';
import { validate, maxLengthValid, isValidAddressLine, notEmptyString } from '../../middleware/validator';
import { isPostcodeValid, formatPostcode } from '../signup/address';
import { TRACK_EVENT } from '../../middleware/analytics';
import { ORDERING_CHANGE_DELIVERY_ADDRESS } from '../../../../server/services/analytics-events';
import { addressLineMaxLen } from '../../../../config/utils';

// ACTIONS
export const FETCH_USER_REQUEST = 'USER/FETCH_USER_REQUEST';
export const FETCH_USER_RECEIVE = 'USER/FETCH_USER_RECEIVE';
export const FETCH_USER_FAILURE = 'USER/FETCH_USER_FAILURE';
export const FETCH_USER_REQUEST_V3 = 'USER/FETCH_USER_REQUEST_V3';
export const FETCH_USER_RECEIVE_V3 = 'USER/FETCH_USER_RECEIVE_V3';
export const FETCH_USER_FAILURE_V3 = 'USER/FETCH_USER_FAILURE_V3';
export const FETCH_USER_MARKETING_CONSENT_REQUEST_V3 = 'USER/FETCH_USER_MARKETING_CONSENT_REQUEST_V3';
export const FETCH_USER_MARKETING_CONSENT_RECEIVE_V3 = 'USER/FETCH_USER_MARKETING_CONSENT_RECEIVE_V3';
export const FETCH_USER_MARKETING_CONSENT_FAILURE_V3 = 'USER/FETCH_USER_MARKETING_CONSENT_FAILURE_V3';

export const FETCH_DELIVERY_ADDRESS_REQUEST =
  'USER/FETCH_DELIVERY_ADDRESS_REQUEST';
export const FETCH_DELIVERY_ADDRESS_RECEIVE =
  'USER/FETCH_DELIVERY_ADDRESS_RECEIVE';
export const FETCH_DELIVERY_ADDRESS_FAILURE =
  'USER/FETCH_DELIVERY_ADDRESS_FAILURE';
export const ACTIVATE_USER_REQUEST = 'USER/ACTIVATE_USER_REQUEST';
export const ACTIVATE_USER_RECEIVE = 'USER/ACTIVATE_USER_RECEIVE';
export const ACTIVATE_USER_FAILURE = 'USER/ACTIVATE_USER_FAILURE';
export const CLEAR_ACTIVATION_ERROR = 'USER/CLEAR_ACTIVATION_ERROR';
export const UPDATE_USER_REQUEST = 'USER/UPDATE_USER_REQUEST';
export const UPDATE_USER_RECEIVE = 'USER/UPDATE_USER_RECEIVE';
export const UPDATE_USER_FAILURE = 'USER/UPDATE_USER_FAILURE';
export const UPDATE_USER_DELIVERY_ADDRESS_REQUEST =
  'USER/UPDATE_USER_DELIVERY_ADDRESS_REQUEST';
export const UPDATE_USER_DELIVERY_ADDRESS_RECEIVE =
  'USER/UPDATE_USER_DELIVERY_ADDRESS_RECEIVE';
export const UPDATE_USER_DELIVERY_ADDRESS_FAILURE =
  'USER/UPDATE_USER_DELIVERY_ADDRESS_FAILURE';
export const CLEAR_UPDATE_USER_DELIVERY_ADDRESS_ERROR =
  'USER/CLEAR_UPDATE_USER_DELIVERY_ADDRESS_ERROR';
export const VALIDATE_USER_DELIVERY_ADDRESS_FAILURE =
  'USER/VALIDATE_USER_DELIVERY_ADDRESS_FAILURE';

// ACTION CREATORS
export const fetchUser = (successRoute, apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me`,
    method: 'GET',
    types: [
      FETCH_USER_REQUEST,
      { type: FETCH_USER_RECEIVE, [ROUTE]: successRoute },
      FETCH_USER_FAILURE,
    ],
    options: { addAuth: true },
  },
});

export const fetchUserV3 = (apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/v3/me`,
    method: 'GET',
    types: [
      FETCH_USER_REQUEST_V3,
      FETCH_USER_RECEIVE_V3,
      FETCH_USER_FAILURE_V3,
    ],
    options: { addAuth: true },
  },
});


export const justFetchUser = (apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me`,
    method: 'GET',
    types: [FETCH_USER_REQUEST, FETCH_USER_RECEIVE, FETCH_USER_FAILURE],
    options: { addAuth: true },
  },
});

export const fetchUserMarketingConsent = (apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/v3/marketing-consent`,
    method: 'GET',
    types: [
      FETCH_USER_MARKETING_CONSENT_REQUEST_V3,
      FETCH_USER_MARKETING_CONSENT_RECEIVE_V3,
      FETCH_USER_MARKETING_CONSENT_FAILURE_V3,
    ],
    options: { addAuth: true },
  },
});

export const registerService = (apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/v3/me/service/pharmacy`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    types: [
      "REGISTER_USER_TYPE_REQUEST","REGISTER_USER_TYPE_RECEVICE","REGISTER_USER_TYPE_FAILURE"
    ],
    options: { addAuth: true },
  },
});

export const activateUser = (apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me/activate`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    types: [
      ACTIVATE_USER_REQUEST,
      { type: ACTIVATE_USER_RECEIVE, [ROUTE]: paths.signup.complete },
      ACTIVATE_USER_FAILURE,
    ],
    options: { addAuth: true },
  },
});

export const activateUserChain = (
  apiEndpoint = PHARMACY_API,
  userEndpoint = USER_API,
) =>
  chainAPIActions(
    activateUser(apiEndpoint),
    () => registerService(userEndpoint),
);

export const changeDeliveryAddress = (
  address,
  redirectRoute,
  apiEndpoint = PHARMACY_API
) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me/addresses/delivery`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      postcode: formatPostcode(address.postcode),
    }),
    types: [
      UPDATE_USER_DELIVERY_ADDRESS_REQUEST,
      {
        type: UPDATE_USER_DELIVERY_ADDRESS_RECEIVE,
        [ROUTE]: redirectRoute,
        [TRACK_EVENT]: ORDERING_CHANGE_DELIVERY_ADDRESS,
        meta: { address },
      },
      UPDATE_USER_DELIVERY_ADDRESS_FAILURE,
    ],
    options: { addAuth: true },
  },
});

export const validateUserDeliveryAddressFailure = (
  address,
  validLine1,
  validLine1MaxLen,
  validLine2,
  validLine2MaxLen,
  validCity,
  validCityMaxLen,
  validPostcode
) => ({
  type: VALIDATE_USER_DELIVERY_ADDRESS_FAILURE,
  address,
  validLine1,
  validLine1MaxLen,
  validLine2,
  validLine2MaxLen,
  validCity,
  validCityMaxLen,
  validPostcode,
});

export const validateUserDeliveryAddress = (address, redirectRoute) => {
  const validPostcode = isPostcodeValid(address.postcode);
  const validLine1 = isValidAddressLine(address.line1);
  const validLine1MaxLen = maxLengthValid(address.line1, addressLineMaxLen);
  const validLine2 = !notEmptyString(address.line2) || isValidAddressLine(address.line2);
  const validLine2MaxLen = maxLengthValid(address.line2, addressLineMaxLen);
  const validCity = isValidAddressLine(address.city);
  const validCityMaxLen = maxLengthValid(address.city, addressLineMaxLen);
  return {
    [validate]: {
      validator: () => validPostcode && validLine1 && validLine1MaxLen && validLine1 && validLine2MaxLen && validCity && validCityMaxLen,
      success: changeDeliveryAddress(address, redirectRoute),
      fail: validateUserDeliveryAddressFailure(
        address,
        validLine1,
        validLine1MaxLen,
        validLine2,
        validLine2MaxLen,
        validCity,
        validCityMaxLen,
        validPostcode
      ),
    },
  };
};

export const clearUpdateDeliveryAddressError = () => ({
  type: CLEAR_UPDATE_USER_DELIVERY_ADDRESS_ERROR,
});

const updateUser = (
  {
    firstName,
    lastName,
    email,
    dob,
    phone,
    addressLine1,
    addressLine2,
    addressTownCity,
    addressPostcode,
    gpId,
    pharmacyId,
    emailConsent,
  },
  apiEndpoint = PHARMACY_API
) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user: {
        firstName,
        lastName,
        email,
        dateOfBirth: dob.toISOString(),
        phone,
      },
      addresses: [
        {
          addressType: 'home',
          line1: addressLine1,
          line2: addressLine2,
          city: addressTownCity,
          postcode: formatPostcode(addressPostcode),
        },
      ],
      practice: {
        organisationCode: gpId,
      },
      pharmacyId: pharmacyId || undefined,
      marketing: {
        emailConsent: Boolean(emailConsent),
      },
    }),
    types: [UPDATE_USER_REQUEST, UPDATE_USER_RECEIVE, UPDATE_USER_FAILURE],
    options: { addAuth: true },
  },
});

export const createUser = (
  {
    firstName,
    lastName,
    dob,
    phone,
    addressLine1,
    addressLine2,
    addressTownCity,
    addressPostcode,
    gpId,
    email,
    pharmacyId,
    emailConsent,
  },
  apiEndpoint = PHARMACY_API
) =>
  chainAPIActions(
    updateUser(
      {
        firstName,
        lastName,
        dob,
        phone,
        addressLine1,
        addressLine2,
        addressTownCity,
        addressPostcode,
        gpId,
        email,
        pharmacyId,
        emailConsent,
      },
      apiEndpoint
    ),
    () => fetchUser('/', apiEndpoint),
  );

export const clearActivationError = () => ({ type: CLEAR_ACTIVATION_ERROR });

export const fetchDeliveryAddress = (apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me/addresses/delivery`,
    method: 'GET',
    types: [
      FETCH_DELIVERY_ADDRESS_REQUEST,
      FETCH_DELIVERY_ADDRESS_RECEIVE,
      FETCH_DELIVERY_ADDRESS_FAILURE,
    ],
    options: { addAuth: true },
  },
});

// SELECTORS
export const STATUS_NOT_SIGNED_UP = 'STATUS_NOT_SIGNED_UP';
export const STATUS_WAITING = 'STATUS_WAITING';
export const STATUS_ACTIVE = 'STATUS_ACTIVE';
export const STATUS_APPROVED = 'STATUS_APPROVED';
export const getStatus = (state) => state.status;
export const getUpdateError = (state) => state.updateError;
export const getActivationError = (state) => state.activationError;
export const getEmailVerifiedAt = (state) => state.emailVerifiedAt;

export const getIsSignedUp = (state) =>
  [STATUS_WAITING, STATUS_APPROVED, STATUS_ACTIVE].includes(getStatus(state));

export const getIsApproved = (state) =>
  [STATUS_APPROVED, STATUS_ACTIVE].includes(getStatus(state));
export const getIsActive = (state) => getStatus(state) === STATUS_ACTIVE;
export const getEmailVerified = (state) => !!getEmailVerifiedAt(state);

export const getDeliveryAddress = (state) => state.deliveryAddress;
export const getUpdateDeliveryAddressError = (state) =>
  state.updateDeliveryAddressError;
export const getDeliveryAddressErrors = (state) =>
  state.deliveryAddressErrors || {};
export const getDeliveryAddressLine1Error = (state) =>
  getDeliveryAddressErrors(state).line1;
export const getDeliveryAddressLine2Error = (state) =>
  getDeliveryAddressErrors(state).line2;
export const getDeliveryTownCityError = (state) =>
  getDeliveryAddressErrors(state).townCity;
export const getDeliveryPostcodeError = (state) =>
  getDeliveryAddressErrors(state).postcode;
export const getAssignedPharmacyId = (state) => state.assignedPharmacyId;
export const getHomeAddress = (state) => state.homeAddress;
export const getEmail = (state) => state.email;
export const getPhone = (state) => state.phone;
export const getFirstName = (state) => state.firstName;
export const getLastName = (state) => state.lastName;

export const getLoaded = (state) => state.isLoaded || false;

// REDUCERS
const status = handleActions(
  {
    [FETCH_USER_RECEIVE]: (state, action) => {
      switch (action.payload.status) {
        case 'active':
          return STATUS_ACTIVE;
        case 'approved':
          return STATUS_APPROVED;
        default:
          return STATUS_WAITING;
      }
    },
    [FETCH_USER_FAILURE]: (state, action) => {
      if (getAPIErrorCode(action) === 'user_not_found')
        return STATUS_NOT_SIGNED_UP;
      return null;
    },
    [ACTIVATE_USER_RECEIVE]: () => STATUS_ACTIVE,
  },
  null
);

const v3User = handleActions(
  {
    [FETCH_USER_REQUEST_V3]: () => null,
    [FETCH_USER_RECEIVE_V3]: (_, action) =>  action.payload || {},
    [FETCH_USER_FAILURE_V3]: () => null,
  },
  null
);

const v3UserMarketingConsent = handleActions(
  {
    [FETCH_USER_MARKETING_CONSENT_REQUEST_V3]: () => null,
    [FETCH_USER_MARKETING_CONSENT_RECEIVE_V3]: (_, action) => action.payload || {},
    [FETCH_USER_MARKETING_CONSENT_FAILURE_V3]: () => null,
  },
  null
);

const emailVerifiedAt = handleActions(
  {
    [FETCH_USER_REQUEST_V3]: () => null,
    // eslint-disable-next-line react/destructuring-assignment
    [FETCH_USER_RECEIVE_V3]: (_, action) =>  action.payload.emailVerifiedAt || null,
    [FETCH_USER_FAILURE_V3]: () => null,
  },
  null
);

const activationError = handleActions(
  {
    [ACTIVATE_USER_REQUEST]: () => null,
    [CLEAR_ACTIVATION_ERROR]: () => null,
    [ACTIVATE_USER_FAILURE]: (state, action) =>
      getAPIErrorMessage(action) || 'Something went wrong.',
  },
  null
);

const updateError = handleActions(
  {
    [UPDATE_USER_REQUEST]: () => null,
    [UPDATE_USER_FAILURE]: (state, action) => getAPIErrorMessage(action),
  },
  null
);

const deliveryAddress = handleActions(
  {
    [FETCH_DELIVERY_ADDRESS_RECEIVE]: (state, action) => ({
      line1: action.payload.line1,
      line2: action.payload.line2,
      city: action.payload.city,
      postcode: action.payload.postcode,
    }),
    [UPDATE_USER_DELIVERY_ADDRESS_RECEIVE]: (state, action) =>
      action.meta.address,
    [VALIDATE_USER_DELIVERY_ADDRESS_FAILURE]: (state, action) => ({
      ...action.address,
    }),
  },
  {
    line1: '',
    line2: '',
    city: '',
    postcode: '',
  }
);

const updateDeliveryAddressError = handleActions(
  {
    [UPDATE_USER_DELIVERY_ADDRESS_FAILURE]: (state, action) =>
      getAPIErrorMessage(action) || 'Something went wrong.',
    [UPDATE_USER_DELIVERY_ADDRESS_REQUEST]: () => null,
    [CLEAR_UPDATE_USER_DELIVERY_ADDRESS_ERROR]: () => null,
  },
  null
);

const deliveryAddressErrors = handleActions(
  {
    [VALIDATE_USER_DELIVERY_ADDRESS_FAILURE]: (state, action) => ({
      line1: action.validLine1 ? (action.validLine1MaxLen ? null : 'Character limit exceeded. Please continue address on line 2.'): 'Address cannot contain special characters',
      line2: action.validLine2 ? (action.validLine2MaxLen ? null : 'Character limit exceeded'): 'Address cannot contain special characters',
      townCity: action.validCity ? (action.validCityMaxLen ? null : 'Character limit exceeded') : 'Address cannot contain special characters',
      postcode: action.validPostcode ? null : 'Postcode is invalid',
    }),
    [UPDATE_USER_DELIVERY_ADDRESS_REQUEST]: () => null,
    [FETCH_DELIVERY_ADDRESS_RECEIVE]: () => null,
  },
  null
);

const assignedPharmacyId = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.assignedPharmacyId,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export const isLoaded = handleActions(
  {
    [FETCH_USER_REQUEST]: () => false,
    [FETCH_USER_RECEIVE]: () => true,
    [FETCH_USER_FAILURE]: () => false,
  },
  false
);

export const homeAddress = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.homeAddress,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export const email = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.email,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export const phone = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.phone,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export const firstName = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.firstName,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export const lastName = handleActions(
  {
    [FETCH_USER_REQUEST]: () => null,
    [FETCH_USER_RECEIVE]: (_, action) => action.payload.lastName,
    [FETCH_USER_FAILURE]: () => null,
  },
  null
);

export default combineReducers({
  status,
  emailVerifiedAt,
  updateError,
  activationError,
  deliveryAddress,
  deliveryAddressErrors,
  updateDeliveryAddressError,
  assignedPharmacyId,
  homeAddress,
  email,
  phone,
  firstName,
  lastName,
  isLoaded,
  v3User,
  v3UserMarketingConsent,
});
